import {DynamicParametersType} from '../dynamicParameters/dynamicParameters.enums';

/**
 * Text justification options for AI content production text specs
 */
export enum TextJustification {
  Left = 0,
  Center = 1,
  Right = 2,
}

export enum AiContentVariationColumnDataType {
  Text = 0,
  Number = 1,
  Color = 2,
  Image = 3,
  Video = 4,
  Persona = 5,
  Language = 6,
}

/**
 * Natural language enum for LLM interaction.
 * Use getMatchingEnumValue to convert between this and AiContentVariationColumnDataType.
 */
export enum AiAiContentVariationColumnDataType {
  Text = 'Text',
  Number = 'Number',
  Color = 'Color',
  Image = 'Image',
  Video = 'Video',
  Persona = 'Persona',
  Language = 'Language',
}

export interface RemoteBriefTargetPlatform {
  platformId: number;
  platformName?: string | null;
}

export interface RemoteAiContentBrief {
  objective?: string | null;
  description?: string | null;
  targetPlatforms?: RemoteBriefTargetPlatform[] | null;
  personaIds?: number[] | null;
  applyBrandGuidelines: boolean;
}

export interface RemoteAiContentVariationColumn {
  name?: string | null;
  dataType: AiContentVariationColumnDataType;
  source?: string | null;
  isRequired: boolean;
  usedFor?: string | null;
}

export interface RemoteAiContentVariationInputSchema {
  columns?: RemoteAiContentVariationColumn[] | null;
}

/**
 * Focal rectangle (non-obscured / safe content area)
 */
export interface RemoteAiContentFocalRect {
  /**
   * X coordinate of the focal rectangle
   */
  x: number;

  /**
   * Y coordinate of the focal rectangle
   */
  y: number;

  /**
   * Width of the focal rectangle
   */
  width: number;

  /**
   * Height of the focal rectangle
   */
  height: number;
}

/**
 * Text-specific specifications
 */
export interface RemoteAiContentTextSpec {
  /**
   * Font size in pixels
   */
  fontSize: number;

  /**
   * Line height multiplier
   */
  lineHeight: number;

  /**
   * URL to the font file
   */
  fontFileUrl?: string | null;

  /**
   * Text box width in pixels
   */
  textBoxWidth: number;

  /**
   * Text box height in pixels
   */
  textBoxHeight: number;

  /**
   * Layer name in the template
   */
  layerName: string;

  /**
   * Text justification
   */
  justification?: TextJustification | null;
}

/**
 * Image-specific specifications
 */
export interface RemoteAiContentImageSpec {
  /**
   * Image width
   */
  width: number;

  /**
   * Image height
   */
  height: number;

  layerName: string;

  /**
   * Non-obscured / safe content area (focal rectangle)
   */
  focalRect?: RemoteAiContentFocalRect | null;
}

/**
 * Video-specific specifications
 */
export interface RemoteAiContentVideoSpec {
  width: number;
  height: number;
  duration: number;//Duration in seconds
  focalRect?: RemoteAiContentFocalRect | null;//Non-obscured / safe content area (focal rectangle)
}

/**
 * Dynamic parameter specifications for a specific ad size
 */
export interface RemoteAiContentContentFeedColumnParamsBySize {
  /**
   * Text-specific requirements for this param in this ad size
   */
  textSpec?: RemoteAiContentTextSpec | null;

  /**
   * Image-specific requirements for this param in this ad size
   */
  imageSpec?: RemoteAiContentImageSpec | null;

  /**
   * Video-specific requirements for this param in this ad size
   */
  videoSpec?: RemoteAiContentVideoSpec | null;
}

/**
 * Content feed column dynamic parameter specification
 */
export interface RemoteAiContentContentFeedColumnParams {
  /**
   * Content feed column name (taxonomy key) for Content source dynamic parameters (e.g. "headline", "hero_image")
   */
  contentFeedColumnName: string;

  /**
   * Clinch dynamic parameter type enum
   */
  type: DynamicParametersType;

  /**
   * Free text describing what this param is used for
   */
  purpose: string;

  /**
   * Per-ad-size specifications for this dynamic param
   * Key is ad size (e.g. "300x250"), value is the specs
   */
  specsByAdSize: Record<string, RemoteAiContentContentFeedColumnParamsBySize>;

  /**
   * AI content generation setup configuration
   */
  generationSetup?: RemoteAiContentGenerationSetup | null;
}

/**
 * Content feed columns with their specifications per ad size
 * Key is content feed column name (taxonomy key), value is the column specs
 */
export interface RemoteAiContentContentFeedColumnSpecs {
  /**
   * Content feed columns with their specifications per ad size
   * Key is content feed column name (taxonomy key), value is the column specs
   */
  columns: Record<string, RemoteAiContentContentFeedColumnParams>;
}

/**
 * Ad sizes (e.g. ["300x250", "728x90", "1080x1920"])
 */
export interface RemoteAiContentTemplateMetadata {
  adSizes: string[];

  /**
   * Content feed columns with their specifications per ad size
   * Key is content feed column name (taxonomy key), value is the column specs
   */
  contentFeedColumns: RemoteAiContentContentFeedColumnSpecs;
}

/**
 * Used input column for AI content generation
 */
export interface RemoteAiContentUsedInputColumn {
  columnName: string;
}

/**
 * Image smart crop setup configuration
 */
export interface RemoteAiContentImageSmartCropSetup {
  inputColumnName?: string | null;
  objectDetectPrompt?: string | null;
  marginPercent: number;
  /** When true, extends image with AI outpainting if detected object lacks sufficient margin. */
  isAiExtendImageIfNeeded?: boolean;
  /** When true, uses realtime smart crop URL (image resizer) instead of static cropped assets. */
  useRealtimeSmartCrop?: boolean;
}

/**
 * Image AI generation setup configuration
 */
export interface RemoteAiContentImageAiGenerationSetup {
  usedInputColumns?: RemoteAiContentUsedInputColumn[] | null;
  vendorService?: number | null;
  vendorServiceWithReferences?: number | null;
  useSharedIntermediateImage?: boolean; // When true, uses shared intermediate image flow
  /** When true, uses realtime smart crop URL (image resizer) instead of static cropped assets. */
  useRealtimeSmartCrop?: boolean;
}

export enum AiContentTextConsistencyMode {
  Identical = 0,
  Adapted = 1,
}

/**
 * Text generation setup configuration
 */
export interface RemoteAiContentTextGenerationSetup {
  usedInputColumns?: RemoteAiContentUsedInputColumn[] | null;
  textConsistencyMode?: AiContentTextConsistencyMode;
  adaptedAdSizes?: string[] | null;
}

/**
 * Product shot generation setup configuration
 */
export interface RemoteAiContentProductShotSetup {
  productImageInputColumnName?: string | null;
  ScenePromptInputColumnName?: RemoteAiContentUsedInputColumn[] | null;
  OptionalSceneReferenceImageInputColumnName?: string | null;
  marginPercent: number;
  productType: number;
  vendorService?: number | null;
  useSharedIntermediateImage?: boolean; // When true, uses shared intermediate image flow
  /** When true, uses realtime smart crop URL (image resizer) instead of static cropped assets. */
  useRealtimeSmartCrop?: boolean;
}

export enum RemoteImageEditMode {
  General = 0,
  ConditionChange = 10,
  HumanStylingChange = 20,
  SceneCleanup = 30,
  BrandAlignment = 40,
  LocalizedContextAdaptation = 50,
  SeasonalContext = 60,
  EnvironmentEnhancement = 70,
  CommercialReadiness = 80,
}

/** String-valued mirror of RemoteImageEditMode for use in AI tool schemas. */
export enum AiImageEditMode {
  General = 'General',
  ConditionChange = 'ConditionChange',
  HumanStylingChange = 'HumanStylingChange',
  SceneCleanup = 'SceneCleanup',
  BrandAlignment = 'BrandAlignment',
  LocalizedContextAdaptation = 'LocalizedContextAdaptation',
  SeasonalContext = 'SeasonalContext',
  EnvironmentEnhancement = 'EnvironmentEnhancement',
  CommercialReadiness = 'CommercialReadiness',
}

export const imageEditModeUiNames: Record<RemoteImageEditMode, string> = {
  [RemoteImageEditMode.General]: 'General',
  [RemoteImageEditMode.ConditionChange]: 'Condition Change',
  [RemoteImageEditMode.HumanStylingChange]: 'Human Styling Change',
  [RemoteImageEditMode.SceneCleanup]: 'Scene Cleanup',
  [RemoteImageEditMode.BrandAlignment]: 'Brand Alignment',
  [RemoteImageEditMode.LocalizedContextAdaptation]: 'Localized Context Adaptation',
  [RemoteImageEditMode.SeasonalContext]: 'Seasonal Context',
  [RemoteImageEditMode.EnvironmentEnhancement]: 'Environment Enhancement',
  [RemoteImageEditMode.CommercialReadiness]: 'Commercial Readiness',
};

export const imageEditModeDescriptions: Record<RemoteImageEditMode, string> = {
  [RemoteImageEditMode.General]: 'General-purpose edit that does not fit any specific category.',
  [RemoteImageEditMode.ConditionChange]: 'Modify environmental conditions while preserving the scene: weather, sky, time of day, lighting, and atmospheric effects (fog, haze, rain).',
  [RemoteImageEditMode.HumanStylingChange]: "Edit people's appearance without changing identity or pose: clothing, colors, accessories, and minor grooming (e.g. sleeves, jackets, sunglasses).",
  [RemoteImageEditMode.SceneCleanup]: 'Remove or reduce unwanted elements: extra people, small objects, clutter, distractions — while maintaining natural background continuity.',
  [RemoteImageEditMode.BrandAlignment]: 'Adapt visuals to brand guidelines: color palette shifts, product recoloring, signage/text replacement, subtle styling adjustments across the scene.',
  [RemoteImageEditMode.LocalizedContextAdaptation]: 'Adjust the scene to match a target geography or audience: language on signs, culturally relevant details, minor environmental cues (flags, driving side hints).',
  [RemoteImageEditMode.SeasonalContext]: 'Transform the scene across seasons: vegetation changes, snow, dryness, foliage color — combined with matching lighting and atmosphere.',
  [RemoteImageEditMode.EnvironmentEnhancement]: 'Improve natural elements without adding new objects: richer greenery, clearer water, cleaner skies, enhanced textures — keeping realism intact.',
  [RemoteImageEditMode.CommercialReadiness]: 'Prepare an image for campaign use: combine cleanup + lighting + brand alignment + minor composition tweaks to create a polished, ad-ready asset.',
};

/**
 * Image edit setup configuration
 */
export interface RemoteAiContentImageEditSetup {
  inputImageColumnName?: string | null;
  stylePromptInputColumns?: RemoteAiContentUsedInputColumn[] | null;
  editMode: RemoteImageEditMode;
}

/**
 * AI content generation setup configuration
 */
export interface RemoteAiContentGenerationSetup {
  imageSmartCrop?: RemoteAiContentImageSmartCropSetup | null;
  imageAiGeneration?: RemoteAiContentImageAiGenerationSetup | null;
  productShotGeneration?: RemoteAiContentProductShotSetup | null;
  textGeneration?: RemoteAiContentTextGenerationSetup | null;
  imageEdit?: RemoteAiContentImageEditSetup | null;
}

/**
 * Request payload for updating generation setup
 */
export interface UpdateGenerationSetupRequest {
  contentFeedColumnName?: string | null;
  generationSetup: RemoteAiContentGenerationSetup;
}
